/**
 * The tabs the viewer is offered on the unified dashboard — the shell and the
 * Bitrix24 tab strip ask the same (#1337). Who gets the shell at all lives in
 * `unified-flag.ts`: `/dashboard` imports that one, and this file carries the
 * layout table with it.
 */
import { useMemo } from 'react';
import { useScreenSubject } from '@/contexts/ScreenSubjectContext';
import { accessUnchecked, viewerCapabilities } from './capabilities';
import { visibleSections } from './layout';
import type { UnifiedSection, UnifiedView } from './unified-search';

/**
 * The tabs the viewer is offered in a view. «My access» may not be known:
 *
 * - `pending` — still on its way: a grant may narrow the set, but
 *   `viewerCapabilities` answers «all» meanwhile, so nothing may be drawn (or
 *   hidden) by that answer;
 * - `unchecked` (not returned: the shell reads it itself) — the read failed
 *   with a retry on offer: nobody knows whether the viewer holds a grant, and
 *   the shell covers the screen with «не удалось проверить права». The strip
 *   must not offer the six tabs over that cover.
 *
 * Either way `sections` is empty: one rule for the strip and the shell, which
 * then asks no domain read for a tab nobody knows the viewer may open.
 */
export function useUnifiedSections(view: UnifiedView = 'business'): {
  sections: UnifiedSection[];
  pending: boolean;
} {
  const subject = useScreenSubject();
  const pending = !!subject && !subject.isOwner && subject.access.status === 'loading';
  const unchecked = accessUnchecked(subject);
  const sections = useMemo(
    () => visibleSections(viewerCapabilities(subject), view),
    [subject, view]
  );
  return { sections: pending || unchecked ? [] : sections, pending };
}
